import { useEffect, useState } from 'react';

import { Divider, type TypeSelectOption, dialogService, toastService } from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { PageTitle } from '~/components/common';
import {
  DeliveryAgencyExcelDownloadModal,
  DeliveryAgencyOrderDetailModal,
  DeliveryAgencyOrderExcelUploadFailModal,
  DeliveryAgencyOrderExcelUploadModal,
  DeliveryAgencyOrderFilter,
  DeliveryAgencyOrderTable,
} from '~/components/delivery-agency';
import { PageWrapper } from '~/components/layout';

import { useDownloadReady, useModal, useStableCallback, useToggle } from '~/hooks/common';
import {
  deliveryAgencyKeys,
  useGetOrdersOrderIdMutation,
  useGetOrdersQuery,
  useGetOrdersSearchConditionsQuery,
  useGetTemplatesDownloadUrlMutation,
  usePostOrdersCancelMutation,
  usePostOrdersDownloadMutation,
} from '~/hooks/delivery-agency/queries';
import { addIf, renderFilterErrorMessage, transformToSelectOptions } from '~/utils/common';
import { formatDate, getDateByOffset } from '~/utils/date';

import {
  BREAD_CRUMB,
  DELIVERY_AGENCY,
  DELIVERY_AGENCY_ORDER_STATUS,
  DELIVERY_AGENCY_PRODUCT_NAME_KEYWORD_TYPE,
  DELIVERY_AGENCY_SORT_BY,
  INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
  INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
  MAX_DELIVERY_AGENCY_DATE_RANGE,
  OPTION_ALL,
  PATH,
  TOAST_MESSAGE,
  TypeDeliveryAgencyOrderStatus,
  TypeDeliveryAgencySortBy,
} from '~/constants';

import type {
  TypeDeliveryAgencyOrder,
  TypeDeliveryAgencyOrderDetail,
  TypeDeliveryAgencyOrderFilterOptions,
  TypeDeliveryAgencyOrderFilterValues,
  TypeDeliveryAgencyOrderFilterValuesStatus,
  TypeDeliveryAgencyOrdersUploadResult,
  TypeGetOrdersParams,
  TypePostOrdersDownloadParams,
} from '~/types/delivery-agency.types';

const getInitialSearchFilterValues = (): TypeDeliveryAgencyOrderFilterValues => ({
  keywordType: null,
  keyword: '',
  deliveryDate: { start: getDateByOffset(1, 'day'), end: getDateByOffset(1, 'day') },
  orderStatus: OPTION_ALL,
  deliveryType: OPTION_ALL,
  courier: OPTION_ALL,
  pagination: {
    currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
    totalCount: 0,
    pageSize: INITIAL_DELIVERY_AGENCY_PAGE_SIZE,
  },
  sortBy: DELIVERY_AGENCY_SORT_BY.CREATED_AT_DESC,
});

const DeliveryAgencyOrderPage = () => {
  const queryClient = useQueryClient();
  const { data: searchConditionsData, isError: isSearchConditionsError } = useGetOrdersSearchConditionsQuery({
    showGlobalErrorToast: false,
  });
  const orderDetailMutation = useGetOrdersOrderIdMutation();
  const templatesDownloadUrlMutation = useGetTemplatesDownloadUrlMutation();
  const ordersCancelMutation = usePostOrdersCancelMutation();
  const ordersDownloadMutation = usePostOrdersDownloadMutation();

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const { isDownloadReady, searchedFilter, markDownloadReady, checkFilterChange } = useDownloadReady({
    initialFilter: getInitialSearchFilterValues(),
  });

  const [searchFilterOptions, setSearchFilterOptions] = useState<TypeDeliveryAgencyOrderFilterOptions>({
    searchKeywordTypes: [],
    orderStatuses: [],
    deliveryTypes: [],
    courierCodes: [],
  });

  const [searchFilterValues, setSearchFilterValues] =
    useState<TypeDeliveryAgencyOrderFilterValues>(getInitialSearchFilterValues);
  const [searchFilterValuesStatus, setSearchFilterValuesStatus] = useState<TypeDeliveryAgencyOrderFilterValuesStatus>({
    keyword: 'default',
    deliveryDate: 'default',
  });

  const [ordersQueryParams, setOrdersQueryParams] = useState<TypeGetOrdersParams | null>(null);
  const ordersQuery = useGetOrdersQuery({
    params: ordersQueryParams ?? ({} as TypeGetOrdersParams),
    enabled: !!ordersQueryParams,
  });

  const [selectedItems, setSelectedItems] = useState<TypeDeliveryAgencyOrder[]>([]);

  const [orderDetailData, setOrderDetailData] = useState<TypeDeliveryAgencyOrderDetail | null>(null);
  const [orderDetailOrderId, setOrderDetailOrderId] = useState<number | null>(null);

  const [orderExcelUploadFailData, setOrderExcelUploadFailData] = useState<TypeDeliveryAgencyOrdersUploadResult | null>(
    null
  );

  useEffect(() => {
    if (!searchConditionsData) return;

    setSearchFilterOptions({
      searchKeywordTypes: transformToSelectOptions(searchConditionsData.searchKeywordTypes, 'code', 'description'),
      orderStatuses: [
        OPTION_ALL,
        ...transformToSelectOptions(searchConditionsData.orderStatuses, 'code', 'description'),
      ],
      deliveryTypes: [
        OPTION_ALL,
        ...transformToSelectOptions(searchConditionsData.deliveryTypes, 'code', 'description'),
      ],
      courierCodes: [OPTION_ALL, ...transformToSelectOptions(searchConditionsData.courierCodes, 'code', 'description')],
    });

    setSearchFilterValues({
      ...getInitialSearchFilterValues(),
      keywordType: transformToSelectOptions(searchConditionsData.searchKeywordTypes, 'code', 'description')[0] ?? null,
    });
  }, [searchConditionsData]);

  const handleFilterChange = useStableCallback(() => checkFilterChange(searchFilterValues));

  useEffect(() => {
    handleFilterChange();
  }, [searchFilterValues, handleFilterChange]);

  useEffect(() => {
    if (isSearchConditionsError) {
      toastService.addToast({ type: 'error', persist: true, message: renderFilterErrorMessage() });
    }
  }, [isSearchConditionsError]);

  const syncOrdersResult = useStableCallback(() => {
    if (!ordersQuery.data || ordersQuery.isPlaceholderData) return;

    const { pagination, data } = ordersQuery.data;
    const updatedFilterValues = {
      ...searchFilterValues,
      pagination: {
        ...searchFilterValues.pagination,
        totalCount: pagination.listTotal,
        pageSize: pagination.listView,
        currentPage: pagination.cursor,
      },
    };
    setSearchFilterValues(updatedFilterValues);
    markDownloadReady({ filterValue: updatedFilterValues, hasData: data.length > 0 });
  });

  useEffect(() => {
    syncOrdersResult();
  }, [ordersQuery.dataUpdatedAt, ordersQuery.isPlaceholderData, syncOrdersResult]);

  const deliveryOrderDetailModal = useModal();

  const orderExcelDownloadModal = useModal();

  const orderExcelUploadModal = useModal({
    onConfirm: (data?: TypeDeliveryAgencyOrdersUploadResult) => {
      if (!data) return;

      if (data.failCount > 0) {
        setOrderExcelUploadFailData(data);
        orderExcelUploadFailModal.handleOpenModal();
      } else {
        toastService.addToast({ message: '등록되었습니다.\n목록에 표시되지 않으면 새로고침 후 확인해 주세요.' });
        handleSearch();
      }
    },
  });

  const orderExcelUploadFailModal = useModal();

  const handleResetFilterValues = () => {
    setSearchFilterValues({
      ...getInitialSearchFilterValues(),
      keywordType: searchFilterOptions.searchKeywordTypes[0] ?? null,
      pagination: searchFilterValues.pagination,
      sortBy: searchFilterValues.sortBy,
    });
    setSearchFilterValuesStatus({
      keyword: 'default',
      deliveryDate: 'default',
    });
  };

  const handleChangePageLimit = (value: TypeSelectOption<number>) => {
    handleSearch({
      ...searchFilterValues,
      pagination: {
        ...searchFilterValues.pagination,
        currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
        pageSize: value.value,
      },
    });
  };

  const handleChangePage = (value: number) => {
    handleSearch({
      ...searchFilterValues,
      pagination: { ...searchFilterValues.pagination, currentPage: value },
    });
  };

  const handleChangeSort = (value: TypeDeliveryAgencySortBy) => {
    handleSearch({
      ...searchFilterValues,
      sortBy: value,
      pagination: { ...searchFilterValues.pagination, currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR },
    });
  };

  const handleChangeRowSelection = (selectedRows: TypeDeliveryAgencyOrder[]) => {
    setSelectedItems(selectedRows);
  };

  const handleSearch = (filterValues: TypeDeliveryAgencyOrderFilterValues = searchFilterValues) => {
    const isDateRangeEmpty = !filterValues.deliveryDate?.start && !filterValues.deliveryDate?.end;

    if (filterValues.keywordType?.value === DELIVERY_AGENCY_PRODUCT_NAME_KEYWORD_TYPE && isDateRangeEmpty) {
      dialogService.addDialog({ children: '배송예정일을 입력 후 검색해 주세요.' });
      setSearchFilterValuesStatus((prev) => ({ ...prev, deliveryDate: 'error' }));
      return;
    }

    if (filterValues.keyword.trim() === '' && isDateRangeEmpty) {
      dialogService.addDialog({
        children: '[출고번호, 판매처 주문번호, 수령자명, 주문자명, 배송예정일] 중 1개 이상 입력 후 검색해주세요.',
      });
      return;
    }

    const dateDiff = dayjs(filterValues.deliveryDate?.end).diff(filterValues.deliveryDate?.start, 'day');
    const isDateLimitExceeded = dateDiff >= MAX_DELIVERY_AGENCY_DATE_RANGE;

    if (isDateRangeEmpty || isDateLimitExceeded) {
      dialogService.addDialog({
        children: `최대 ${MAX_DELIVERY_AGENCY_DATE_RANGE}일로 입력 후 검색해주세요.`,
      });
      setSearchFilterValuesStatus((prev) => ({ ...prev, deliveryDate: 'error' }));
      return;
    }

    const params: TypeGetOrdersParams = {
      searchKeywordType: filterValues.keywordType?.value ?? '',
      searchKeyword: filterValues.keyword,
      ...addIf(formatDate(filterValues.deliveryDate?.start, 'YYYY-MM-DD'), 'startDate'),
      ...addIf(formatDate(filterValues.deliveryDate?.end, 'YYYY-MM-DD'), 'endDate'),
      ...addIf(filterValues.orderStatus?.value, 'orderStatus'),
      ...addIf(filterValues.deliveryType?.value, 'deliveryType'),
      ...addIf(filterValues.courier?.value, 'courierCode'),
      cursor: filterValues.pagination.currentPage,
      limit: filterValues.pagination.pageSize,
      sortBy: filterValues.sortBy,
    };

    setSearchFilterValues(filterValues);
    setSelectedItems([]);

    queryClient.invalidateQueries({
      queryKey: deliveryAgencyKeys.orders(params),
    });
    setOrdersQueryParams(params);
  };

  const handleOpenDetailModal = (order: TypeDeliveryAgencyOrder) => {
    orderDetailMutation.mutate(
      { orderId: order.orderId },
      {
        onSuccess: (data) => {
          setOrderDetailData(data);
          setOrderDetailOrderId(order.orderId);
          deliveryOrderDetailModal.handleOpenModal();
        },
      }
    );
  };

  const handleOrderRequestCancel = () => {
    if (selectedItems.length === 0) {
      return;
    }

    const unCancelableStatusCodes = new Set<TypeDeliveryAgencyOrderStatus>([
      DELIVERY_AGENCY_ORDER_STATUS.ON_THE_WAY,
      DELIVERY_AGENCY_ORDER_STATUS.DELIVERY_COMPLETED,
      DELIVERY_AGENCY_ORDER_STATUS.CANCELED,
      DELIVERY_AGENCY_ORDER_STATUS.DELIVERY_FAILED,
    ]);

    const isNothingCancelable = selectedItems.every((item) => unCancelableStatusCodes.has(item.orderStatus.code));
    if (isNothingCancelable) {
      dialogService.addDialog({
        children: `선택한 주문 ${selectedItems.length}건 모두 주문취소 불가한 주문입니다.`,
      });
      return;
    }

    const unCancelableItems = selectedItems.filter((item) => unCancelableStatusCodes.has(item.orderStatus.code));
    if (unCancelableItems.length > 0) {
      dialogService.addDialog({
        type: 'confirm',
        children: `취소 불가한 주문이 ${unCancelableItems.length}개 포함되어 있습니다.\n불가 건 제외 후 취소를 진행하시겠습니까?\n취소 후 상태를 다시 되돌릴 수 없습니다.`,
        onConfirm: () => {
          const cancelableItems = selectedItems.filter((item) => !unCancelableStatusCodes.has(item.orderStatus.code));
          cancelOrderRequest(cancelableItems);
        },
      });
      return;
    }

    dialogService.addDialog({
      type: 'confirm',
      children: `선택한 주문 ${selectedItems.length}건을 주문취소하시겠습니까?\n취소 후 상태를 다시 되돌릴 수 없습니다.`,
      onConfirm: () => {
        cancelOrderRequest(selectedItems);
      },
    });
  };

  const cancelOrderRequest = (requestItems: TypeDeliveryAgencyOrder[]) => {
    ordersCancelMutation.mutate(
      { orderIds: requestItems.map((item) => item.orderId) },
      {
        onSuccess: (data) => {
          if (data === null) {
            toastService.addToast({ message: '주문취소되었습니다.' });
          } else {
            dialogService.addDialog({ children: data.message });
          }

          setSelectedItems([]);
          handleSearch();
        },
        onError: (error) => {
          if (error?.code === DELIVERY_AGENCY.BACKEND_ERROR_DIALOG_CODE) {
            dialogService.addDialog({ children: error?.message });
            return;
          }

          toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  const handleOpenOrderExcelUpload = () => {
    orderExcelUploadModal.handleOpenModal();
  };

  const handleExcelTemplateDownload = () => {
    templatesDownloadUrlMutation.mutate(
      { type: 'ORDER_UPLOAD' },
      {
        onSuccess: (url) => {
          window.open(url, '_blank', 'noopener,noreferrer');
          toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS });
        },
      }
    );
  };

  const handleConfirmExcelDownload = (reason: string) => {
    const params: TypePostOrdersDownloadParams = {
      downloadReason: reason,
      input: {
        searchKeywordType: searchedFilter.keywordType?.value ?? '',
        searchKeyword: searchedFilter.keyword,
        ...addIf(formatDate(searchedFilter.deliveryDate?.start, 'YYYY-MM-DD'), 'startDate'),
        ...addIf(formatDate(searchedFilter.deliveryDate?.end, 'YYYY-MM-DD'), 'endDate'),
        ...addIf(searchedFilter.orderStatus?.value, 'orderStatus'),
        ...addIf(searchedFilter.deliveryType?.value, 'deliveryType'),
        ...addIf(searchedFilter.courier?.value, 'courierCode'),
      },
    };

    ordersDownloadMutation.mutate(params, {
      onSuccess: () => {
        orderExcelDownloadModal.handleConfirmModal();
        toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS });
      },
    });
  };

  return (
    <PageWrapper>
      <PageTitle
        title="배송 주문 관리"
        breadcrumbs={BREAD_CRUMB[PATH.DELIVERY_AGENCY_ORDER]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <>
          <DeliveryAgencyOrderFilter
            options={searchFilterOptions}
            values={searchFilterValues}
            valuesStatus={searchFilterValuesStatus}
            onChange={(values) => setSearchFilterValues((prev) => ({ ...prev, ...values }))}
            onChangeStatus={(status) => setSearchFilterValuesStatus((prev) => ({ ...prev, ...status }))}
            onSearch={() =>
              handleSearch({
                ...searchFilterValues,
                pagination: {
                  ...searchFilterValues.pagination,
                  currentPage: INITIAL_DELIVERY_AGENCY_PAGE_CURSOR,
                },
              })
            }
            onReset={handleResetFilterValues}
          />
          <Divider color="gray100" />
        </>
      )}
      <DeliveryAgencyOrderTable
        data={ordersQuery.data?.data ?? null}
        pagination={searchFilterValues.pagination}
        sortBy={searchFilterValues.sortBy}
        selectedItems={selectedItems}
        isDownloadReady={isDownloadReady}
        onChangePageLimit={handleChangePageLimit}
        onChangeSort={handleChangeSort}
        onChangePage={handleChangePage}
        onChangeRowSelection={handleChangeRowSelection}
        onClickOrderRequestCancel={handleOrderRequestCancel}
        onClickViewDetail={handleOpenDetailModal}
        onClickOrderExcelDownload={orderExcelDownloadModal.handleOpenModal}
        onClickOrderExcelUpload={handleOpenOrderExcelUpload}
        onClickExcelTemplateDownload={handleExcelTemplateDownload}
      />
      {deliveryOrderDetailModal.isOpen && orderDetailData && orderDetailOrderId !== null && (
        <DeliveryAgencyOrderDetailModal
          orderId={orderDetailOrderId}
          data={orderDetailData}
          open={deliveryOrderDetailModal.isOpen}
          onClose={deliveryOrderDetailModal.handleCloseModal}
        />
      )}
      {orderExcelDownloadModal.isOpen && (
        <DeliveryAgencyExcelDownloadModal
          title="주문 정보 전체 다운로드"
          open={orderExcelDownloadModal.isOpen}
          onCancel={orderExcelDownloadModal.handleCloseModal}
          onConfirm={(reason) => handleConfirmExcelDownload(reason)}
        />
      )}
      {orderExcelUploadModal.isOpen && (
        <DeliveryAgencyOrderExcelUploadModal
          open={orderExcelUploadModal.isOpen}
          onCancel={orderExcelUploadModal.handleCloseModal}
          onConfirm={orderExcelUploadModal.handleConfirmModal}
        />
      )}
      {orderExcelUploadFailModal.isOpen && orderExcelUploadFailData && (
        <DeliveryAgencyOrderExcelUploadFailModal
          data={orderExcelUploadFailData}
          open={orderExcelUploadFailModal.isOpen}
          onClose={orderExcelUploadFailModal.handleCloseModal}
        />
      )}
    </PageWrapper>
  );
};

export default DeliveryAgencyOrderPage;
